// Data Contracts
import {
  DisplayFieldOfGuid,
  FilingAccessTypeConfiguration,
  JudgeReviewFiling,
  JudgeReviewFilingConfiguration,
  JudgeReviewUserSetting,
  PersonName,
} from '@data-contracts';

// Local
import {
  JudgeReviewStudioAccessOption,
  JudgeReviewStudioHeader,
  JudgeReviewStudioOption,
  JudgeReviewUploadTarget,
} from '../models/judge-review-studio.view-model';
import {
  findDocumentActionConfig,
  resolveJudicialActionName,
} from './judge-review-config.utils';
import { personDisplayName } from './person-display-name.util';
import { actionDocuments } from './judge-review-action.utils';

/**
 * Pure mappers from the judge-review `@data-contracts` DTOs to the Review & Sign
 * studio UI models. This is the single translation boundary for the studio's
 * display bindings — the component calls these, the template consumes UI models
 * only. No Angular dependencies.
 */

/**
 * Maps the primary document's disposition into the studio header chip label,
 * e.g. "GRANT · PROPOSED ORDER". Reads the per-document action config from the
 * filing configuration; returns null when nothing is set. Keeps the
 * contract-reading in the mapper rather than inline in the container.
 */
export function mapDispositionLabel(
  filing: JudgeReviewFiling | null | undefined,
  config: JudgeReviewFilingConfiguration | null | undefined,
): string | null {
  const doc = actionDocuments(filing)[0] ?? filing?.judgeReviewDocuments?.[0];
  if (!doc) {
    return null;
  }
  const docConfig = findDocumentActionConfig(config, doc.existingDocumentId);
  const action = resolveJudicialActionName(
    config,
    doc.existingDocumentId,
    doc.selectedJudicialActionId,
  );
  const type = docConfig?.filingType ?? doc.judgeReviewFilingType ?? undefined;
  const parts = [action, type].filter(Boolean).map(p => p!.toUpperCase());
  return parts.length ? parts.join(' · ') : null;
}

/**
 * Maps the blob-upload slots the server reserved for a given document on the
 * upload PATCH into the studio's {@link JudgeReviewUploadTarget} UI model.
 * Returns null when the document is not present on the patched filing. This is
 * the single boundary where the studio's upload orchestration reads the
 * `JudgeReviewFiling` DTO — the container consumes UI fields only.
 *
 * @param filing The server's patched filing.
 * @param existingDocumentId The document whose upload slots are needed.
 */
export function mapUploadTarget(
  filing: JudgeReviewFiling | null | undefined,
  existingDocumentId: number | null,
): JudgeReviewUploadTarget | null {
  const doc = actionDocuments(filing).find(
    d => d.existingDocumentId === existingDocumentId,
  );
  if (!doc) {
    return null;
  }
  return {
    documentUploadUri: doc.documentUploadUri ?? null,
    documentStoreId: doc.judgeReviewDocumentStoreId ?? null,
  };
}

/**
 * Signature name for the studio (reviewing judge; the current user when a clerk
 * acts). Uses the same {@link personDisplayName} precedence as the studio header
 * — honouring `fullName` — with a "Judge" fallback so the header and the placed
 * signature never render the same name differently.
 */
export function mapSignatureName(name: PersonName | null | undefined): string {
  return personDisplayName(name) || 'Judge';
}

/**
 * Signature initials, e.g. "S.L." Derived from the same resolved display name as
 * {@link mapSignatureName} (first + last token) so `fullName`-only names still
 * produce initials. Returns an em dash when no name is available.
 */
export function mapSignatureInitials(
  name: PersonName | null | undefined,
): string {
  const tokens = personDisplayName(name).split(/\s+/).filter(Boolean);
  if (!tokens.length) {
    return '—';
  }
  const first = tokens[0][0] ?? '';
  const last = tokens.length > 1 ? (tokens[tokens.length - 1][0] ?? '') : '';
  const letters = (first + last).toUpperCase();
  return letters ? letters.split('').join('.') + '.' : '—';
}

/** Maps the filing DTO into the studio header/context UI model. */
export function mapStudioHeader(
  filing: JudgeReviewFiling | null | undefined,
): JudgeReviewStudioHeader | null {
  if (!filing) {
    return null;
  }
  const firstCase = filing.judgeReviewCases?.[0];
  return {
    reviewerName: personDisplayName(filing.judgeReviewerName),
    courtName: filing.courtName ?? '',
    hasCase: !!firstCase,
    caseName: firstCase?.caseName ?? '',
    caseNumber: firstCase?.caseNumber ?? '',
  };
}

/**
 * Maps the available filing-type options for the Document Type select. Options
 * without an id are dropped so the template can `track` on the id without risking
 * an NG0955 duplicate-key crash (two null ids) — a null-id option is not
 * selectable anyway.
 */
export function mapFilingTypeOptions(
  options: DisplayFieldOfGuid[] | null | undefined,
): JudgeReviewStudioOption[] {
  return (options ?? [])
    .filter(option => option.value != null)
    .map(option => ({
      id: option.value ?? null,
      label: option.name ?? '',
    }));
}

/** Maps the access-type options for the Security / Access select (drops null-id options; see {@link mapFilingTypeOptions}). */
export function mapAccessTypeOptions(
  accessTypes: FilingAccessTypeConfiguration[] | null | undefined,
): JudgeReviewStudioAccessOption[] {
  return (accessTypes ?? [])
    .filter(accessType => accessType.accessTypeId != null)
    .map(accessType => ({
      id: accessType.accessTypeId ?? null,
      label: accessType.accessType ?? '',
    }));
}

/** Maps the judge's user settings into Status / Action select options (drops null-id options; see {@link mapFilingTypeOptions}). */
export function mapJudicialActionOptions(
  settings: JudgeReviewUserSetting[] | null | undefined,
): JudgeReviewStudioOption[] {
  return (settings ?? [])
    .filter(setting => setting.judicialActionId != null)
    .map(setting => ({
      id: setting.judicialActionId ?? null,
      label: setting.judicialAction ?? '',
    }));
}

